import { Injectable, computed, inject, signal } from '@angular/core';
import type { PlanCatalog } from '../models/plan-catalog.model';
import type { PlanSlug, PlanTier } from '../models/subscription-status.enum';
import { BillingApiService } from '../services/billing-api.service';

@Injectable({ providedIn: 'root' })
export class PlansStore {
  private readonly api = inject(BillingApiService);

  private readonly plansSignal = signal<readonly PlanCatalog[]>([]);
  private readonly loadingSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);
  private loadingPromise: Promise<void> | null = null;

  readonly plans = this.plansSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly hasPlans = computed(() => this.plansSignal().length > 0);

  constructor() {
    void this.refresh();
  }

  refresh(): Promise<void> {
    if (this.loadingPromise) return this.loadingPromise;
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    this.loadingPromise = (async () => {
      try {
        const plans = await this.api.getPlans();
        this.plansSignal.set(plans);
      } catch (error) {
        this.errorSignal.set(this.resolveErrorMessage(error));
      } finally {
        this.loadingSignal.set(false);
        this.loadingPromise = null;
      }
    })();
    return this.loadingPromise;
  }

  findPlan(slug: PlanSlug | null | undefined): PlanCatalog | null {
    if (!slug) return null;
    return this.plansSignal().find((p) => p.slug === slug) ?? null;
  }

  findPlanByTier(tier: PlanTier | null | undefined): PlanCatalog | null {
    if (!tier) return null;
    return this.plansSignal().find((p) => p.tier === tier) ?? null;
  }

  comparePlanTier(slugA: PlanSlug, slugB: PlanSlug): number {
    const a = this.findPlan(slugA)?.tierOrder ?? 0;
    const b = this.findPlan(slugB)?.tierOrder ?? 0;
    return a - b;
  }

  private resolveErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'message' in error) {
      const message = (error as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
    return 'billing.errors.loadFailed';
  }
}
